import { supabaseReservation } from "../integrations/supabase/client";

export class AudioRecorder {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;

  constructor(private onAudioData: (audioData: Float32Array) => void) {}

  async start() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      this.audioContext = new AudioContext({
        sampleRate: 24000,
      });

      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        this.onAudioData(new Float32Array(inputData));
      };

      this.source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }

  stop() {
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

export class RealtimeChat {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private audioEl: HTMLAudioElement;
  private conversationId: string | null = null;
  private outletsCache: Array<{ id: string; name: string }> = [];
  private maxDurationTimer: ReturnType<typeof setTimeout> | null = null;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private lastActivityAtMs: number | null = null;

  private isEnding = false;
  private onAutoEnd: ((reason: "maxDuration" | "inactivity") => void) | null = null;

  // Billable time tracking - only charge when AI is speaking
  private billableSeconds = 0;
  private aiSpeakingStart: number | null = null;

  // Active time tracking (AI speaking + user speaking) for display-only duration
  private userSpeakingStart: number | null = null;
  private userSpeakingSeconds = 0;

  // Optional account_id for embed widgets
  private embedAccountId: string | null = null;

  private static MAX_CALL_DURATION_MS = 10 * 60 * 1000; // 10 minutes
  private static INACTIVITY_TIMEOUT_MS = 30 * 1000; // 30 seconds

  // Text-only mode skips microphone and audio playback
  private textOnlyMode = false;

  constructor(
    private onMessage: (event: any) => void,
    onAutoEnd?: (reason: "maxDuration" | "inactivity") => void,
    embedAccountId?: string | null,
    textOnly?: boolean
  ) {
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
    this.onAutoEnd = onAutoEnd ?? null;
    this.embedAccountId = embedAccountId ?? null;
    this.textOnlyMode = textOnly ?? false;
  }

  private startBillableTime() {
    if (this.aiSpeakingStart === null) {
      this.aiSpeakingStart = Date.now();
      console.log("[RealtimeChat] Billable time started");
    }
  }

  private stopBillableTime() {
    if (this.aiSpeakingStart !== null) {
      const elapsed = Math.ceil((Date.now() - this.aiSpeakingStart) / 1000);
      this.billableSeconds += elapsed;
      this.aiSpeakingStart = null;
      console.log(`[RealtimeChat] Billable time stopped, added ${elapsed}s, total: ${this.billableSeconds}s`);
    }
  }

  private startUserSpeech() {
    if (this.userSpeakingStart === null) {
      this.userSpeakingStart = Date.now();
    }
  }

  private stopUserSpeech() {
    if (this.userSpeakingStart !== null) {
      const elapsed = Math.ceil((Date.now() - this.userSpeakingStart) / 1000);
      this.userSpeakingSeconds += elapsed;
      this.userSpeakingStart = null;
    }
  }

  private getBillableSeconds(): number {
    let total = this.billableSeconds;
    if (this.aiSpeakingStart !== null) {
      total += Math.ceil((Date.now() - this.aiSpeakingStart) / 1000);
    }
    return total;
  }

  private getUserSpeakingSeconds(): number {
    let total = this.userSpeakingSeconds;
    if (this.userSpeakingStart !== null) {
      total += Math.ceil((Date.now() - this.userSpeakingStart) / 1000);
    }
    return total;
  }

  private getActiveSeconds(): number {
    return this.getUserSpeakingSeconds() + this.getBillableSeconds();
  }

  private clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private markActivity(source: string) {
    this.lastActivityAtMs = Date.now();
    console.log(`[RealtimeChat] activity: ${source}`);

    // Reset inactivity timeout
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(async () => {
      if (this.isEnding) return;
      console.log("[RealtimeChat] Inactivity timeout reached, ending call...");
      await this.disconnect();
      this.onAutoEnd?.("inactivity");
    }, RealtimeChat.INACTIVITY_TIMEOUT_MS);
  }

  async init() {
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();

    const logStep = (label: string) => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const ms = Math.round(now - startedAt);
      console.log(`[RealtimeChat] ${label} (+${ms}ms)`);
    };

    try {
      logStep("init start");

      // Get user's account_id first for proper RLS
      // For embed widgets, use the embedAccountId passed via constructor
      let userAccountId: string | null = this.embedAccountId;
      
      if (!userAccountId) {
        try {
          const { data: { user } } = await supabaseReservation.auth.getUser();
          if (user) {
            const { data: profile } = await supabaseReservation
              .from("profiles")
              .select("account_id")
              .eq("user_id", user.id)
              .maybeSingle();
            userAccountId = profile?.account_id ?? null;
          }
        } catch (e) {
          console.warn("Failed to get user account_id:", e);
        }
      }
      
      console.log("[RealtimeChat] Using account_id:", userAccountId);

      // Start DB write early (doesn't need to block the realtime connection)
      const conversationPromise = supabaseReservation
        .from("conversations")
        .insert({ 
          status: "active",
          account_id: userAccountId 
        })
        .select("id")
        .single();

      const ensureConversationId = async () => {
        if (this.conversationId) return this.conversationId;
        try {
          const { data: conv, error: convErr } = await conversationPromise;
          if (convErr) throw convErr;
          this.conversationId = conv.id;
          console.log("Conversation created:", this.conversationId);
          return this.conversationId;
        } catch (e) {
          console.warn("Failed to create conversation record (continuing):", e);
          return null;
        }
      };

      // Get ephemeral token (server builds initial instructions, but the client later sends session.update)
      logStep("requesting ephemeral token");
      const { data: tokenData, error: tokenError } = await supabaseReservation.functions.invoke("realtime-session", {
        body: { account_id: userAccountId }
      });

      if (tokenError || !tokenData?.client_secret?.value) {
        throw new Error("Failed to get ephemeral token");
      }

      const EPHEMERAL_KEY = tokenData.client_secret.value as string;
      logStep("ephemeral token received");

      // Create peer connection
      this.pc = new RTCPeerConnection();

      this.pc.oniceconnectionstatechange = () => {
        console.log("[RealtimeChat] ICE state:", this.pc?.iceConnectionState);
      };
      this.pc.onconnectionstatechange = () => {
        console.log("[RealtimeChat] PC state:", this.pc?.connectionState);
      };

      // Set up remote audio (only for voice mode)
      if (!this.textOnlyMode) {
        this.pc.ontrack = (e) => (this.audioEl.srcObject = e.streams[0]);
      }

      // Add local audio track only for voice mode (this is often the slowest part due to permission prompt)
      if (!this.textOnlyMode) {
        logStep("requesting microphone access");
        const ms = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 24000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        logStep("microphone stream ready");
        this.pc.addTrack(ms.getTracks()[0]);
      } else {
        logStep("text-only mode - skipping microphone");
        // For text-only mode, we still need to add a dummy audio track for WebRTC to work
        // Create a silent audio track
        const audioContext = new AudioContext();
        const oscillator = audioContext.createOscillator();
        const destination = audioContext.createMediaStreamDestination();
        oscillator.connect(destination);
        oscillator.start();
        const silentTrack = destination.stream.getAudioTracks()[0];
        // Mute it immediately
        silentTrack.enabled = false;
        this.pc.addTrack(silentTrack);
        logStep("silent audio track added for text-only mode");
      }

      // Set up data channel
      this.dc = this.pc.createDataChannel("oai-events");

      this.dc.addEventListener("open", () => {
        console.log("[RealtimeChat] data channel open");

        // Start max duration timer when connection opens
        this.startMaxDurationTimer();

        // Start inactivity timer immediately (covers "user started call but stayed idle")
        this.markActivity("connected");
      });

      this.dc.addEventListener("close", () => {
        console.log("[RealtimeChat] data channel closed");
      });

      this.dc.addEventListener("message", async (e) => {
        const event = JSON.parse(e.data);
        console.log("Received event:", event);
        this.onMessage(event);

        // Track billable + active time, and reset inactivity timer on real activity
        // WebRTC audio goes via media track, but these events still arrive on the data channel.
        if (event.type === "response.created") {
          this.startBillableTime();
          this.markActivity("ai_response_started");
          console.log("[RealtimeChat] AI response started - billing timer ON");
        } else if (event.type === "response.done") {
          this.stopBillableTime();
          this.markActivity("ai_response_done");
          console.log("[RealtimeChat] AI response done - billing timer OFF");
        } else if (event.type === "input_audio_buffer.speech_started") {
          this.startUserSpeech();
          this.markActivity("user_speech_started");
        } else if (
          event.type === "conversation.item.input_audio_transcription.completed" ||
          event.type === "input_audio_buffer.speech_stopped"
        ) {
          this.stopUserSpeech();
          this.markActivity("user_speech_stopped");
        }

        // Handle session.created - send configuration (THIS OVERRIDES server-side instructions)
        if (event.type === "session.created") {
          logStep("session.created received; preparing session.update");

          // Get the user's account_id first
          let accountId: string | null = this.embedAccountId;
          const { data: { user } } = await supabaseReservation.auth.getUser();
          
          if (!accountId && user) {
            const { data: profile } = await supabaseReservation
              .from("profiles")
              .select("account_id")
              .eq("user_id", user.id)
              .maybeSingle();
            accountId = profile?.account_id ?? null;
          }

          // Fetch config filtered by account_id
          const [{ data: config }, { data: outlets }, { data: blockedDates }] = await Promise.all([
            accountId 
              ? supabaseReservation.from("agent_config").select("*").eq("account_id", accountId).maybeSingle()
              : supabaseReservation.from("agent_config").select("*").limit(1).maybeSingle(),
            accountId
              ? supabaseReservation.from("outlets").select("id, name, address, phone, hours").eq("account_id", accountId).eq("is_active", true)
              : supabaseReservation.from("outlets").select("id, name, address, phone, hours").eq("is_active", true),
            accountId
              ? supabaseReservation.from("blocked_dates").select("*, outlets(name)").eq("account_id", accountId)
              : supabaseReservation.from("blocked_dates").select("*, outlets(name)"),
          ]);

          // Cache outlets for later tool calls
          this.outletsCache = (outlets ?? []).map((o: any) => ({ id: o.id as string, name: o.name as string }));

          const menuContent = config?.menu || "";
          const reservationsEnabled = config?.reservations_enabled !== false;

          let blockedDatesInfo = "";
          if (blockedDates && (blockedDates as any[]).length > 0) {
            const blockedList = (blockedDates as any[])
              .map((bd) => {
                const outletName = bd?.outlets?.name;
                const outletInfo = outletName ? ` (${outletName} only)` : " (all outlets)";
                if (bd.is_full_day) {
                  return `${bd.block_date}${outletInfo}${bd.reason ? ": " + bd.reason : ""}`;
                }
                return `${bd.block_date} at ${bd.block_time}${outletInfo}${bd.reason ? " (" + bd.reason + ")" : ""}`;
              })
              .join(", ");
            blockedDatesInfo = `\n\nBLOCKED DATES (cannot accept reservations): ${blockedList}. If a customer asks for a blocked date, politely explain that date is not available and suggest an alternative.`;
          }

          let outletsInfo = "";
          if (outlets && (outlets as any[]).length > 0) {
            const outletsList = (outlets as any[])
              .map((o) => {
                let info = `- ${o.name}`;
                if (o.address) info += ` (${o.address})`;
                if (o.hours) info += ` - Hours: ${o.hours}`;
                return info;
              })
              .join("\n");

            if ((outlets as any[]).length > 1) {
              outletsInfo = `\n\nAVAILABLE OUTLETS (${(outlets as any[]).length} locations):\n${outletsList}\n\nIMPORTANT: When taking a reservation, you MUST ask which outlet/location the customer wants to book at. We have ${(outlets as any[]).length} outlets and the customer must choose one.`;
            } else {
              outletsInfo = `\n\nOUR OUTLET:\n${outletsList}`;
            }
          }

          let instructions: string;
          
          if (reservationsEnabled) {
            instructions = config
              ? `You are a receptionist for ${config.restaurant_name}.

RESERVATION CAPABILITY: YES - You CAN and SHOULD help customers make reservations. This is one of your primary functions.

Hours: ${config.restaurant_hours}
Menu: ${menuContent}
${config.instructions}${blockedDatesInfo}${outletsInfo}

CRITICAL RESERVATION FLOW - YOU MUST FOLLOW THIS EXACTLY:
1. When a customer wants to make a reservation, first ask which outlet/location they'd like to book at (if there are multiple outlets).
2. When a customer provides ALL reservation details (name, email, date, time, guests, AND outlet), you MUST IMMEDIATELY say OUT LOUD: "Thank you for that information. Please give me just a moment while I book your reservation." 
3. SAY THIS ACKNOWLEDGMENT FIRST before calling create_reservation. The customer needs to hear something so they know you're working on it.
4. ONLY AFTER speaking the acknowledgment, call the create_reservation function with all details including the outlet_name.
5. If a date is blocked, tell them it's not available and ask for an alternative.
6. After successful booking, confirm all the details back to the customer INCLUDING which outlet they've booked at.

NEVER call create_reservation silently - ALWAYS speak to acknowledge you're processing the request FIRST.`
              : `You are a helpful restaurant receptionist.${blockedDatesInfo}${outletsInfo}`;
          } else {
            instructions = config
              ? `You are a helpful assistant for ${config.restaurant_name}.

RESERVATION CAPABILITY: NO - You CANNOT take reservations. Do NOT offer to make reservations or ask for reservation details. If a customer asks about reservations, immediately and clearly explain that reservations are not available through this system.

Hours: ${config.restaurant_hours}
Menu: ${menuContent}
${config.instructions}${outletsInfo}

IMPORTANT: You are here to help customers with questions about our business, products, services, and operational hours. You DO NOT handle reservations. If a customer asks to make a reservation, IMMEDIATELY tell them that reservations are not available through this phone line and suggest they contact the restaurant directly through other channels or visit our website.`
              : `You are a helpful restaurant receptionist. You do NOT handle reservations.${outletsInfo}`;

          }

          // Only include reservation tools if reservations are enabled
          const tools = reservationsEnabled
            ? [
                {
                  type: "function",
                  name: "create_reservation",
                  description: "Create a restaurant reservation",
                  parameters: {
                    type: "object",
                    properties: {
                      name: { type: "string" },
                      email: { type: "string", description: "Customer email address" },
                      date: { type: "string", description: "YYYY-MM-DD" },
                      time: { type: "string", description: "HH:MM" },
                      guests: { type: "number" },
                      outlet_name: {
                        type: "string",
                        description:
                          "The outlet/location name the customer chose (must match one of the AVAILABLE OUTLETS names exactly).",
                      },
                    },
                    required: ["name", "email", "date", "time", "guests", "outlet_name"],
                  },
                },
              ]
            : [];

          const session = this.textOnlyMode
            ? {
                modalities: ["text"],
                instructions,
                tools,
                tool_choice: "auto",
                temperature: 0.8,
              }
            : {
                modalities: ["text", "audio"],
                instructions,
                voice: "alloy",
                input_audio_format: "pcm16",
                output_audio_format: "pcm16",
                input_audio_transcription: { model: "whisper-1" },
                turn_detection: {
                  type: "server_vad",
                  threshold: 0.5,
                  prefix_padding_ms: 300,
                  silence_duration_ms: 1000,
                },
                tools,
                tool_choice: "auto",
                temperature: 0.8,
              };

          this.dc!.send(JSON.stringify({ type: "session.update", session }));
          logStep("session.update sent, reservations enabled: " + reservationsEnabled);
        }

        // Handle function calls
        if (event.type === "response.function_call_arguments.done") {
          const callId = (event as any).call_id as string;
          const args = JSON.parse((event as any).arguments);

          const outletName = String(args.outlet_name ?? "").trim();
          const outlet = this.outletsCache.find((o) => o.name.toLowerCase() === outletName.toLowerCase());

          if (!outlet && this.outletsCache.length > 0) {
            const available = this.outletsCache.map((o) => o.name).join(", ");
            this.dc!.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify({
                    success: false,
                    error: "invalid_outlet",
                    message: `Please ask the customer to choose an outlet. Available outlets are: ${available}.`,
                  }),
                },
              })
            );
            this.dc!.send(JSON.stringify({ type: "response.create" }));
            return;
          }

          // Validate guests - must be at least 1
          const guestsCount = Number(args.guests) || 1;
          if (guestsCount < 1) {
            this.dc!.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify({
                    success: false,
                    error: "invalid_guests",
                    message: "Please confirm the number of guests with the customer. It must be at least 1.",
                  }),
                },
              })
            );
            this.dc!.send(JSON.stringify({ type: "response.create" }));
            return;
          }

          // Ensure conversation exists before writing reservation
          await ensureConversationId();

          const { data: resv, error: resvErr } = await supabaseReservation
            .from("reservations")
            .insert({
              conversation_id: this.conversationId,
              name: args.name,
              email: args.email,
              date: args.date,
              time: args.time,
              guests: guestsCount,
              outlet_id: outlet?.id ?? null,
              status: "confirmed",
            })
            .select()
            .single();

          if (resvErr) {
            console.error("Reservation error:", resvErr);

            // Check if it's a blocked date error from the database trigger
            const isBlockedError =
              (resvErr as any).message?.includes("Reservation blocked") || (resvErr as any).message?.includes("unavailable");

            // Send function call output back to the AI
            this.dc!.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify({
                    success: false,
                    error: isBlockedError ? "date_blocked" : "database_error",
                    message: isBlockedError
                      ? `Sorry, ${args.date} is not available for reservations. Please ask the customer for another date.`
                      : "There was an issue creating the reservation. Please try again.",
                  }),
                },
              })
            );

            // Trigger AI to respond with the error message
            this.dc!.send(JSON.stringify({ type: "response.create" }));
          } else if (resv) {
            // Send function call output for success
            this.dc!.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify({
                    success: true,
                    reservation_id: (resv as any).id,
                    message: `Reservation confirmed for ${args.name} on ${args.date} at ${args.time} for ${args.guests} guests${outletName ? ` at ${outletName}` : ""}.`,
                  }),
                },
              })
            );

            // Trigger AI to respond with the confirmation
            this.dc!.send(JSON.stringify({ type: "response.create" }));

            // Send confirmation email
            const { data: config } = await supabaseReservation.from("agent_config").select("restaurant_name").limit(1).maybeSingle();

            await supabaseReservation.functions.invoke("send-reservation-confirmation", {
              body: {
                name: args.name,
                email: args.email,
                date: args.date,
                time: args.time,
                guests: args.guests,
                restaurantName: config?.restaurant_name || "Restaurant",
              },
            });
          }
        }
      });

      // Create and set local description
      logStep("creating WebRTC offer");
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      logStep("local description set");

      // Connect to OpenAI (SDP exchange) with a timeout so we don't hang forever
      const baseUrl = "https://api.openai.com/v1/realtime";
      const model = "gpt-4o-realtime-preview-2024-12-17";

      logStep("starting SDP exchange");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: "POST",
        body: offer.sdp,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${EPHEMERAL_KEY}`,
          "Content-Type": "application/sdp",
        },
      }).finally(() => clearTimeout(timeoutId));

      if (!sdpResponse.ok) {
        const errTxt = await sdpResponse.text().catch(() => "");
        throw new Error(
          `SDP exchange failed (${sdpResponse.status}). ${errTxt ? `Details: ${errTxt.slice(0, 200)}` : ""}`
        );
      }

      const answerSdp = await sdpResponse.text();
      const answer = {
        type: "answer" as RTCSdpType,
        sdp: answerSdp,
      };

      await this.pc.setRemoteDescription(answer);
      logStep("remote description set (connected)");

      // Finish DB setup (async but we await once here to ensure it's ready for reservations)
      await ensureConversationId();
      logStep("init complete");
    } catch (error) {
      console.error("Error initializing chat:", error);
      throw error;
    }
  }

  async sendText(text: string) {
    if (!this.dc || this.dc.readyState !== "open") {
      throw new Error("Data channel not ready");
    }

    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ type: "input_text", text }],
        },
      })
    );
    this.dc.send(JSON.stringify({ type: "response.create" }));
  }

  private startMaxDurationTimer() {
    // Clear any existing timer
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
    }

    console.log(`[RealtimeChat] Max duration timer started (${RealtimeChat.MAX_CALL_DURATION_MS / 1000}s)`);

    this.maxDurationTimer = setTimeout(async () => {
      if (this.isEnding) return;
      this.isEnding = true;

      console.log("[RealtimeChat] Max call duration reached, ending call...");

      // Send a goodbye message through the AI before disconnecting
      if (this.dc && this.dc.readyState === "open") {
        try {
          this.dc.send(
            JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "message",
                role: "user",
                content: [
                  {
                    type: "input_text",
                    text: "[SYSTEM: The call has reached its maximum duration. Please say a brief goodbye to the caller, something like: 'It looks like we are no longer connected. Thank you for contacting us. Goodbye!' Then end the conversation.]",
                  },
                ],
              },
            })
          );
          this.dc.send(JSON.stringify({ type: "response.create" }));

          setTimeout(async () => {
            await this.disconnect();
            this.onAutoEnd?.("maxDuration");
          }, 5000);
        } catch (e) {
          console.error("[RealtimeChat] Error sending goodbye:", e);
          await this.disconnect();
          this.onAutoEnd?.("maxDuration");
        }
      } else {
        await this.disconnect();
        this.onAutoEnd?.("maxDuration");
      }
    }, RealtimeChat.MAX_CALL_DURATION_MS);
  }

  private clearMaxDurationTimer() {
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
      this.maxDurationTimer = null;
    }
  }

  async disconnect() {
    // Prevent duplicate disconnect calls
    if (this.isEnding) {
      console.log("[RealtimeChat] disconnect() already in progress, skipping");
      return;
    }
    this.isEnding = true;

    // Stop timers
    this.clearMaxDurationTimer();
    this.clearInactivityTimer();

    // Stop any ongoing speaking segments
    this.stopBillableTime();
    this.stopUserSpeech();

    // Freeze final counters
    const finalBillableSeconds = this.getBillableSeconds();
    const finalActiveSeconds = this.getActiveSeconds();

    // Update conversation status with duration and deduct credits
    if (this.conversationId) {
      // First get the conversation to check if already completed
      const { data: conversation } = await supabaseReservation
        .from("conversations")
        .select("started_at, account_id, status, ended_at")
        .eq("id", this.conversationId)
        .single();

      // Only update if conversation is still active (not already completed)
      if (conversation && conversation.status !== "completed") {
        const startTime = new Date(conversation.started_at).getTime();
        const endTime = Date.now();
        const totalDurationSeconds = Math.ceil((endTime - startTime) / 1000);

        // Duration shown to users = active conversation time (user speaking + AI speaking)
        const displayDurationSeconds = finalActiveSeconds;

        console.log(
          `[RealtimeChat] Call ended - Total wall: ${totalDurationSeconds}s, Active: ${displayDurationSeconds}s, Billable(AI): ${finalBillableSeconds}s`
        );

        await supabaseReservation
          .from("conversations")
          .update({
            status: "completed",
            ended_at: new Date().toISOString(),
            duration_seconds: displayDurationSeconds,
          })
          .eq("id", this.conversationId);

        // Only charge for billable time (when AI was actually speaking)
        // $1.20/min = 2 cents/second
        const costCents = Math.round(finalBillableSeconds * 2); // $0.02 per second of AI speaking

        if (conversation.account_id && costCents > 0) {
          try {
            const minutes = Math.floor(finalBillableSeconds / 60);
            const seconds = finalBillableSeconds % 60;
            const billableStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
            
            const { data: result, error: deductError } = await supabaseReservation.rpc("deduct_credits", {
              _account_id: conversation.account_id,
              _amount_cents: costCents,
              _conversation_id: this.conversationId,
              _description: `Voice call - ${billableStr} active`,
            });

            if (deductError) {
              console.error("Failed to deduct credits:", deductError);
            } else {
              console.log("Credits deducted:", result);
            }
          } catch (e) {
            console.error("Error deducting credits:", e);
          }
        } else {
          console.log("[RealtimeChat] No billable time - no credits deducted");
        }
      } else if (conversation?.status === "completed") {
        console.log("[RealtimeChat] Conversation already completed, skipping update");
      }
    }

    this.dc?.close();
    this.pc?.close();
  }
}
